import { PaymentTypes } from 'appConstants';
import { Package } from 'pages/newSubscription/models';
import { UpgradePackageInfo } from 'pages/subscriptions/models';
import { ISchoolInfo } from 'pages/upgradeSubscription/models';

export enum SubscriptionStep {
  CHOOSE_PACKAGE,
  SCHOOL_DETAILS,
  PROFILE_DETAILS,
  PAYMENT_TYPE,
  PAYMENT_DETAILS,
  CONFIRMATION,
}

export interface ISchoolDetails {
  schoolname: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  townCity: string;
  county: string;
  country: string;
  postcodeZipcode: string;
}

export interface ISchoolUser {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
  schoolOfficeFirstName: string;
  schoolOfficeLastName: string;
  schoolOfficeEmailAddress: string;
  bAcceptedTermsAndConditions: boolean;
}

export interface ISchoolUserFreeTrial {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
  bAcceptedTermsAndConditions: boolean;
}

export interface PayByCardFormValues {
  poNumber: string;
  paymentType: PaymentTypes.PAY_BY_CARD | PaymentTypes.PAY_BY_INVOICE;
  package: UpgradePackageInfo | null;
  cardholderName: string;
  card: string;
  validPromoCode: string;
  total: number;
  validDiscount: number;
}

export class SubscribeParser {
  public static createEmptySchoolDetails = (
    schoolInfo: ISchoolInfo | null
  ): ISchoolDetails => ({
    schoolname: schoolInfo ? schoolInfo.schoolName : '',
    phoneNumber: schoolInfo?.phoneNumber ?? '',
    addressLine1: schoolInfo?.addressLine1 ?? '',
    addressLine2: schoolInfo?.addressLine2 ?? '',
    addressLine3: schoolInfo?.addressLine3 ?? '',
    townCity: schoolInfo?.townCity ?? '',
    county: schoolInfo?.county ?? '',
    country: schoolInfo?.country ?? '',
    postcodeZipcode: schoolInfo?.postcodeZipcode ?? '',
  });

  public static createEmptySchoolUser = (): ISchoolUser => ({
    firstName: '',
    lastName: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    schoolOfficeFirstName: '',
    schoolOfficeLastName: '',
    schoolOfficeEmailAddress: '',
    bAcceptedTermsAndConditions: false
  });

  public static createEmptySchoolUserFreeTrial = (): ISchoolUserFreeTrial => ({
    firstName: '',
    lastName: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    bAcceptedTermsAndConditions: false
  });  

  public static buildSchoolDetailsPayload = (values: ISchoolDetails) => ({
    ...values,
    addressLine2: values.addressLine2 ? values.addressLine2 : null,
    addressLine3: values.addressLine3 ? values.addressLine3 : null,
    county: values.county ? values.county : null,
  });

  public static buildSchoolUserDetailsPayload = (
    values: ISchoolUser,
    schoolDetails: ISchoolDetails
  ) => ({
    ...values,
    ...schoolDetails,
    addressLine2: schoolDetails.addressLine2
      ? schoolDetails.addressLine2
      : null,
    addressLine3: schoolDetails.addressLine3
      ? schoolDetails.addressLine3
      : null,
    county: schoolDetails.county ? schoolDetails.county : null,
  });

  public static buildSchoolUserDetailsPayloadFreeTrial = (
    values: ISchoolUserFreeTrial,
    schoolDetails: ISchoolDetails
  ) => ({
    ...values,
    ...schoolDetails,
    addressLine2: schoolDetails.addressLine2
      ? schoolDetails.addressLine2
      : null,
    addressLine3: schoolDetails.addressLine3
      ? schoolDetails.addressLine3
      : null,
    county: schoolDetails.county ? schoolDetails.county : null,
  });  

  public static buildArchiveNowPayload = (
    registrationToken: string,
    promoCode: string,
    packages: Package[],
    expectedGrossAmount: number,
    poNumber: string,
    FinanceEmail: string,
  ) => ({
    registrationToken,
    promoCode,
    expectedGrossAmount,
    packageIDs: packages.map((p: Package) => p.packageID),
    poNumber,
    FinanceEmail,
  });

  public static buildSubscribeByCardPayload = (
    promoCode: string,
    packages: Package[],
    total: number,
    token: any,
    registrationToken: string,
    poNumber: string,
    FinanceEmail: string,
  ) => ({
    registrationToken,
    promoCode,
    packageIDs: packages.map((p: Package) => p.packageID),
    expectedGrossAmount: total,
    nameOnCard: token.card.name,
    cardEndDigits: token.card.last4,
    expiryDate: `${
      token.card.exp_month.toString().length === 1 && '0'
    }${token.card.exp_month.toString()}/${token.card.exp_year.toString()[2]}${
      token.card.exp_year.toString()[3]
    }`,
    stripeToken: token.id,
    poNumber,
    FinanceEmail,
  });

  public static getPageHeaderInfo = (step: any) => {
    switch (step) {
      case SubscriptionStep.CHOOSE_PACKAGE:
        return { title: 'Choose your package', nextStep: 'School details' };

      case SubscriptionStep.SCHOOL_DETAILS:
        return { title: 'Enter school details', nextStep: 'Enter two contact details' };

      case SubscriptionStep.PROFILE_DETAILS:
        return { title: 'Enter two contact details', nextStep: 'Payment type' };

      case SubscriptionStep.PAYMENT_TYPE:
        return {
          title: 'Payment type',
          nextStep: 'Payment details / Confirmation',
        };

      case SubscriptionStep.PAYMENT_DETAILS:
        return { title: 'Payment details', nextStep: '' };

      case SubscriptionStep.CONFIRMATION:
        return { title: 'Confirmation', nextStep: '' };

      default:
        return { title: '', nextStep: '' };
    }
  };

  public static buildUpdateAfter3DSecurePayload = (response: any) => {
    return {
      registrationToken: response.registrationToken,
      tid: response.tid,
      stripePaymentIntentID: response.error
        ? response.error.payment_intent.id
        : response.paymentIntent.id,
    };
  };

  public static parseError = (errors: any) => {
    let error: string;
    switch (errors[0].messageCode) {
      case 'Invalid_PromoCode_Expired':
        error = 'Promo code is expired';
        break;
      case 'Exisiting_SChoolName_PostcodeZipcode_Combination':
        error =
          'Hey! Your school already exists, contact admin@getset4education so we can get you started.';
        break;
      case 'Missing_PromoCode':
        error = 'Please provide a promo code';
        break;
      case `Invalid_PromoCode`:
        error = `Invalid promo code `;
        break;
      case 'EmailAddress_Already_Exists':
        error = 'Email address already exists';
        break;
      case 'OfficeEmailAddress_Already_Exists':
        error = 'Office email address already exists';
        break;        
      case 'Access_Token_Invalid':
        error = 'Sorry, something went wrong. Please try again. ';
        break;
      case 'ExpectedGrossAmount':
        error = 'Sorry, something went wrong. Please try again. ';
        break;
      case 'pkey_general_payment_card_error':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_error':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_subscription_failed':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_failed':
        error = 'Payment failed. Please try again.';
        break;
      case 'Invalid_PromoCode_NotStarted':
        error = 'Invalid Promo code';
        break;
      default:
        error = 'Sorry, something went wrong. Please try again. ';
    }
    return error;
  };
}
